import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  listMeetings, cancelMeeting, respondToMeeting, notReady, reasonOf, whenOf, lengthOf, canJoin, stateWords,
  recurrenceText, joinUrlOf, kindWords
} from '../../lib/meetings';
import { useMe } from '../../lib/me';
import { CONTRACTOR_ID_KEY, contractorSignedIn } from '../../lib/contractorAuth';
import { Dialog, Faces } from '../todos/TodoPeople';
import { Icon } from '../todos/icons';
import MeetingForm from './MeetingForm';
import { StartNowButton, RecordCallButton } from './StartNow';
import './Meetings.css';

/*
 * THE MEETINGS, at /meetings (Gordon, 22 October).
 *
 * Upcoming | Past, one card per meeting: what, when (in Malta, 24-hour), how
 * long, who organised it, the faces of who is coming and how many outside
 * guests, a chip for its state, a badge when it repeats, and - on an
 * invitation - Accept / Tentative / Decline. Join appears in the quarter of
 * an hour before it starts and for as long as it is live, and opens the
 * pre-join page (MeetingPage), not the call: nobody should land in a meeting
 * with the wrong camera on. Afterwards the card says whether there are
 * minutes and a recording.
 *
 * "New meeting" opens the form (MeetingForm). The organiser's own cards
 * carry Edit, Copy link and Cancel; cancelling one meeting of a series asks
 * which - this one, this and the following, all - before anything is sent.
 *
 * Two more buttons (26 October, StartNow.js): START MEETING NOW, the one
 * most pressed with a phone in hand - a meeting for this moment, the
 * recorder open at once, the details filled in while it records - and
 * RECORD A CALL ON ANOTHER APP, for a Teams, Zoom, Slack or phone call that
 * is already happening. Both land as meetings in this list, tagged "Ad hoc".
 *
 * The server decides who sees what; this only asks. A 503 naming
 * migrate:066 means the code is deployed and the tables are not, and gets a
 * calm panel rather than an error.
 */

export const RESPONSES = [
  ['accepted', 'Accept'], ['tentative', 'Tentative'], ['declined', 'Decline']
];
const RESPONSE_WORD = { accepted: 'Going', tentative: 'Maybe', declined: 'Not going' };

/** The person signed in, as the chat names people: 'u:<id>' or 'c:<id>'. */
export const myKeysOf = (me) => {
  const keys = [];
  if (me?.id) keys.push(`u:${me.id}`);
  try {
    const c = localStorage.getItem(CONTRACTOR_ID_KEY);
    if (c && contractorSignedIn()) keys.push(`c:${c}`);
  } catch { /* storage refused */ }
  return keys;
};

export const isOrganiser = (m, me) => Boolean(m && (m.is_organiser === true || m.mine === true
  || (m.organiser_key && myKeysOf(me).includes(m.organiser_key))));

export const StateChip = ({ meeting, now }) => {
  const words = stateWords(meeting, now);
  const tone = words === 'Live' ? 'live' : words === 'Cancelled' ? 'bad' : words === 'Ended' ? 'quiet' : 'info';
  return (
    <span className={`mt-chip is-${tone}`} data-state={words.toLowerCase()}>
      {words === 'Live' && <span className="mt-live-dot" aria-hidden="true" />}
      {words}
    </span>
  );
};

/** The calm panel for a server that has the code and not the tables yet. */
export function NotReadyPanel() {
  return (
    <div className="mt-notready" role="status">
      <h2>Meetings are not switched on here yet</h2>
      <p>
        The server knows about meetings but has not been given the tables to keep them in. Whoever
        runs Jarvis needs to run <code>npm run migrate:066</code> on the server; nothing else is
        required, and nothing has been lost.
      </p>
    </div>
  );
}

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
};

/** "Rita Gatt and 2 outside guests" -> the faces and the count. */
const splitPeople = (m) => {
  const all = Array.isArray(m.attendees) ? m.attendees : [];
  const inside = all.filter((a) => a.kind !== 'guest');
  const guests = all.filter((a) => a.kind === 'guest');
  return { inside, guests, count: m.attendee_count ?? all.length, guestCount: m.guest_count ?? guests.length };
};

function MeetingCard({ m, me, now, onRespond, onEdit, onCancel, onCopied }) {
  const navigate = useNavigate();
  const mine = isOrganiser(m, me);
  const people = splitPeople(m);
  const joinable = canJoin(m, now);
  const words = stateWords(m, now);
  const over = words === 'Ended' || words === 'Cancelled';
  const hasMinutes = m.has_minutes ?? ((m.minutes_count ?? (Array.isArray(m.minutes) ? m.minutes.length : 0)) > 0);
  const hasRecording = m.has_recording ?? ((m.recording_count ?? (Array.isArray(m.recordings) ? m.recordings.length : 0)) > 0);
  const response = m.my_response || null;

  return (
    <li className={`mt-card${words === 'Live' ? ' is-live' : ''}${over ? ' is-over' : ''}`} data-meeting={m.id} data-state={words.toLowerCase()}>
      <div className="mt-card-when" aria-hidden="true">
        <span className="mt-card-day">{whenOf(m.starts_at, m.ends_at, now).split(' · ')[0]}</span>
        <span className="mt-card-time">{whenOf(m.starts_at, m.ends_at, now).split(' · ')[1] || ''}</span>
      </div>
      <div className="mt-card-main">
        <div className="mt-card-top">
          <Link to={`/meetings/${m.id}`} className="mt-card-title">{m.title || 'Meeting'}</Link>
          <StateChip meeting={m} now={now} />
          {m.recurrence && (
            <span className="mt-chip is-quiet mt-recurring" title={m.recurrence_text || recurrenceText(m.recurrence, now)}>
              <Icon name="repeat" size={12} /> Recurring
            </span>
          )}
          {m.kind === 'room' && <span className="mt-chip is-quiet"><Icon name="home" size={12} /> {m.location || 'In the room'}</span>}
          {m.kind === 'call' && <span className="mt-chip is-quiet mt-call-chip"><Icon name="share" size={12} /> {kindWords(m)}</span>}
          {m.adhoc && <span className="mt-chip is-quiet mt-adhoc-chip" title="Started on the spot, not planned">Ad hoc</span>}
        </div>
        <p className="mt-card-meta">
          <span className="mt-sr">{whenOf(m.starts_at, m.ends_at, now)} · </span>
          {lengthOf(m.starts_at, m.ends_at)}
          {m.organiser_name && <><span className="mt-sep">·</span>{mine ? 'organised by you' : `organised by ${m.organiser_name}`}</>}
          {response && !mine && <><span className="mt-sep">·</span><span className={`mt-response is-${response}`}>{RESPONSE_WORD[response] || response}</span></>}
        </p>
        <div className="mt-card-people">
          <Faces members={people.inside.map((a) => ({ key: a.key || a.name, name: a.name }))} max={5} />
          {people.guestCount > 0 && (
            <span className="mt-guests" title="Outside guests, joining by link">
              <Icon name="person" size={13} /> {people.guestCount} guest{people.guestCount === 1 ? '' : 's'}
            </span>
          )}
          {people.count > 0 && people.inside.length === 0 && people.guestCount === 0 && (
            <span className="mt-guests">{people.count} attending</span>
          )}
        </div>
        {(hasMinutes || hasRecording) && (
          <div className="mt-card-after">
            {hasRecording && <Link to={`/meetings/${m.id}?tab=recording`} className="mt-chip is-link"><Icon name="mic" size={12} /> Recording</Link>}
            {hasMinutes && <Link to={`/meetings/${m.id}?tab=minutes`} className="mt-chip is-link"><Icon name="note" size={12} /> Minutes</Link>}
          </div>
        )}
      </div>
      <div className="mt-card-actions">
        {joinable && (
          <button type="button" className="btn btn-primary tds-btn mt-join" onClick={() => navigate(`/meetings/${m.id}`)}>
            Join
          </button>
        )}
        {!mine && !over && (
          <div className="mt-respond" role="group" aria-label="Your answer">
            {RESPONSES.map(([k, w]) => (
              <button key={k} type="button" className={`mt-respond-btn is-${k}${response === k ? ' is-on' : ''}`}
                aria-pressed={response === k} data-response={k} onClick={() => onRespond(m, k)}>
                {w}
              </button>
            ))}
          </div>
        )}
        {mine && !over && m.kind !== 'online' && !contractorSignedIn() && (
          // A meeting in the room, or a call on another app, is recorded from
          // the device in hand: the live one goes straight back to its recorder.
          <Link to={`/meetings/${m.id}/record`} className={`btn ${words === 'Live' ? 'btn-primary' : 'btn-outline'} tds-btn mt-record-link`}>
            <Icon name="mic" size={14} /> {words === 'Live' ? 'Back to the recorder' : 'Record'}
          </Link>
        )}
        {mine && !over && (
          <div className="mt-organise">
            {m.kind === 'online' && (
              <button type="button" className="tds-link mt-copy" onClick={async () => onCopied(await copyText(joinUrlOf(m)))}>
                <Icon name="link" size={13} /> Copy link
              </button>
            )}
            <button type="button" className="tds-link mt-edit" onClick={() => onEdit(m)}><Icon name="edit" size={13} /> Edit</button>
            <button type="button" className="tds-link is-danger mt-cancel" onClick={() => onCancel(m)}><Icon name="close" size={13} /> Cancel</button>
          </div>
        )}
      </div>
    </li>
  );
}

export default function MeetingsPage() {
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const range = params.get('range') === 'past' ? 'past' : 'upcoming';
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [notThere, setNotThere] = useState(false);
  const [form, setForm] = useState(null); // false | null closed; {} new; meeting edit
  const [cancelling, setCancelling] = useState(null); // meeting
  const [scope, setScope] = useState('this');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [now, setNow] = useState(() => new Date());

  // The Join buttons depend on the clock; once a minute is often enough.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback(async () => {
    try {
      const list = await listMeetings(range);
      setRows(list);
      setError(null);
      setNotThere(false);
    } catch (err) {
      if (notReady(err)) { setNotThere(true); setRows([]); return; }
      setError(reasonOf(err, 'Could not read the meetings.'));
      setRows((r) => r || []);
    }
  }, [range]);

  useEffect(() => { setRows(null); load(); }, [load]);

  const sorted = useMemo(() => (rows || []).slice().sort((a, b) => {
    const d = (Date.parse(a.starts_at) || 0) - (Date.parse(b.starts_at) || 0);
    return range === 'past' ? -d : d;
  }), [rows, range]);

  const setRange = (r) => setParams((p) => {
    const next = new URLSearchParams(p);
    if (r === 'upcoming') next.delete('range'); else next.set('range', r);
    return next;
  }, { replace: true });

  const respond = async (m, response) => {
    const before = rows;
    setRows((cur) => cur.map((x) => (x.id === m.id ? { ...x, my_response: response } : x)));
    try {
      await respondToMeeting(m.id, response);
    } catch (err) {
      setRows(before);
      setError(reasonOf(err, 'Could not send your answer.'));
    }
  };

  const doCancel = async () => {
    if (!cancelling) return;
    setBusy(true);
    try {
      await cancelMeeting(cancelling.id, cancelling.series_id ? scope : 'this');
      setCancelling(null);
      setToast('Meeting cancelled. Everybody invited is told.');
      load();
    } catch (err) {
      setError(reasonOf(err, 'Could not cancel the meeting.'));
      setCancelling(null);
    } finally {
      setBusy(false);
    }
  };

  /**
   * The server says who the invitations reached: sent: [{ recipient, ok,
   * reason }]. "Invitations sent to 4 · 1 failed: …" rather than a cheerful
   * sentence that may not be true for everybody.
   */
  const sentWords = (m) => {
    const sent = Array.isArray(m?.sent) ? m.sent : null;
    if (!sent || !sent.length) return `“${m?.title || 'Meeting'}” is in everybody's calendar.`;
    const ok = sent.filter((s) => s.ok !== false).length;
    const failed = sent.filter((s) => s.ok === false);
    let words = `Invitations sent to ${ok}`;
    if (failed.length) words += ` · ${failed.length} failed: ${failed.map((s) => `${s.recipient || 'somebody'}${s.reason ? ` (${s.reason})` : ''}`).join(', ')}`;
    return words;
  };

  const saved = (m) => {
    setForm(null);
    setToast(form?.id ? 'Saved. Everybody invited gets the change.' : sentWords(m));
    load();
  };

  // Staff and administrators organise meetings; a contractor is invited to
  // them, and sees the page to answer and join without a New meeting button.
  const canOrganise = Boolean(me?.signedIn);

  return (
    <div className="mt-page">
      <header className="mt-head">
        <div className="mt-head-words">
          <h1 className="mt-title">Meetings</h1>
          <p className="mt-sub">
            Online or in the room, with NCMB, contractors and outside guests. Guests join from the invitation
            with their name only and wait to be let in.
          </p>
        </div>
        {canOrganise && (
          <div className="mt-head-actions">
            <StartNowButton onError={setError} />
            <RecordCallButton onError={setError} />
            <button type="button" className="btn btn-outline tds-btn mt-new" onClick={() => setForm({})}>
              <Icon name="plus" size={15} /> New meeting
            </button>
          </div>
        )}
      </header>

      <div className="mt-tools">
        <div className="mt-range" role="tablist" aria-label="Which meetings">
          {[['upcoming', 'Upcoming'], ['past', 'Past']].map(([k, w]) => (
            <button key={k} type="button" role="tab" className="mt-range-btn" aria-selected={range === k} data-range={k}
              onClick={() => setRange(k)}>{w}</button>
          ))}
        </div>
      </div>

      {error && (
        <p className="mt-error" role="alert">
          {error} <button type="button" className="tds-link" onClick={() => setError(null)}>Dismiss</button>
        </p>
      )}

      {notThere ? <NotReadyPanel /> : rows === null ? (
        <p className="mt-quiet">Loading…</p>
      ) : sorted.length === 0 ? (
        <div className="mt-empty">
          <Icon name="calendar" size={28} />
          <p className="mt-empty-lead">{range === 'past' ? 'No meetings yet.' : 'Nothing coming up.'}</p>
          {canOrganise && range === 'upcoming' && (
            <p className="mt-quiet">
              Press <strong>Start meeting now</strong> to record the meeting you are in, <strong>Record a call</strong> for one on Teams, Zoom or
              the phone, or <strong>New meeting</strong> to invite colleagues, contractors and outside guests.
            </p>
          )}
        </div>
      ) : (
        <ul className="mt-list" aria-label={range === 'past' ? 'Past meetings' : 'Upcoming meetings'}>
          {sorted.map((m) => (
            <MeetingCard key={m.id} m={m} me={me} now={now} onRespond={respond}
              onEdit={(x) => setForm(x)} onCancel={(x) => { setScope('this'); setCancelling(x); }}
              onCopied={(ok) => setToast(ok ? 'Join link copied.' : 'Could not copy — copy it from the meeting page instead.')} />
          ))}
        </ul>
      )}

      {form && <MeetingForm meeting={form.id ? form : null} onSaved={saved} onClose={() => setForm(null)} />}

      {cancelling && (
        <Dialog label={`Cancel “${cancelling.title}”?`} onClose={() => setCancelling(null)} className="mt-scope-dialog">
          <p className="tds-quiet">Everybody invited is told, and the invitation is withdrawn from their calendars.</p>
          {cancelling.series_id && (
            <div className="mt-scope" role="radiogroup" aria-label="Which meetings to cancel">
              {[['this', 'This meeting only'], ['following', 'This and the following ones'], ['all', 'All meetings in the series']].map(([k, w]) => (
                <label key={k} className={`mt-scope-opt${scope === k ? ' is-on' : ''}`}>
                  <input type="radio" name="mt-cancel-scope" value={k} checked={scope === k} onChange={() => setScope(k)} />
                  <span>{w}</span>
                </label>
              ))}
            </div>
          )}
          <div className="tds-dialog-foot">
            <button type="button" className="btn btn-outline tds-btn" disabled={busy} onClick={() => setCancelling(null)}>Keep it</button>
            <button type="button" className="btn btn-primary tds-btn mt-cancel-confirm" disabled={busy} onClick={doCancel}>
              {busy ? 'Cancelling…' : 'Cancel the meeting'}
            </button>
          </div>
        </Dialog>
      )}

      {toast && (
        <div className="tds-toast mt-toast" role="status">
          <span>{toast}</span>
          <button type="button" className="tds-x" aria-label="Dismiss" onClick={() => setToast(null)}>
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
